import { auth, onAuthStateChanged } from './firebase';

// Always use relative URLs - Firebase Hosting rewrites /api/** to Cloud Function
// Hosting strips nothing - /api/trees goes to function as /api/trees
// BUT our function routes don't have /api/ prefix
// So hosting rewrite: /api/trees → function receives path /trees ✓
// This is same-origin = no CORS issues

const BASE_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:5001/vanshatree/us-central1/api'
  : '';

const getToken = () => {
  return new Promise((resolve) => {
    const user = auth.currentUser;
    if (user) {
      user.getIdToken(true).then(resolve).catch(() => resolve(null));
      return;
    }
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe();
      if (user) {
        user.getIdToken(true).then(resolve).catch(() => resolve(null));
      } else {
        resolve(null);
      }
    });
    setTimeout(() => resolve(null), 8000);
  });
};

const request = async (method, endpoint, data = null, requireAuth = true, providedToken = null) => {
  const headers = { 'Content-Type': 'application/json' };
  if (requireAuth) {
    const token = providedToken || await getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }
  const url = `${BASE_URL}${endpoint}`;
  const opts = { method, headers };
  if (data && method !== 'GET') opts.body = JSON.stringify(data);
  const res = await fetch(url, opts);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `API error ${res.status}`);
  }
  return res.json();
};

const api = {
  request,
  register: (data, token) => request('POST', '/api/auth/register', data, true, token),
  getMe: (token) => request('GET', '/api/auth/me', null, true, token),
  updateProfile: (data, token) => request('PUT', '/api/auth/profile', data, true, token),
  deleteAccount: () => request('DELETE', '/api/auth/me'),
  getTrees: () => request('GET', '/api/trees'),
  getTree: (id) => request('GET', `/api/trees/${id}`),
  createTree: (data) => request('POST', '/api/trees', data),
  updateTree: (id, data) => request('PUT', `/api/trees/${id}`, data),
  addPerson: (data) => request('POST', '/api/persons', data),
  getPerson: (id) => request('GET', `/api/persons/${id}`, null, false),
  updatePerson: (id, data) => request('PUT', `/api/persons/${id}`, data),
  deletePerson: (id) => request('DELETE', `/api/persons/${id}`),
  addMilestone: (id, data) => request('POST', `/api/persons/${id}/milestone`, data),
  editMilestone: (id, milestoneId, data) => request('PUT', `/api/persons/${id}/milestone/${milestoneId}`, data),
  deleteMilestone: (id, milestoneId) => request('DELETE', `/api/persons/${id}/milestone/${milestoneId}`),
  addAchievement: (id, data) => request('POST', `/api/persons/${id}/achievement`, data),
  editAchievement: (id, achievementId, data) => request('PUT', `/api/persons/${id}/achievement/${achievementId}`, data),
  deleteAchievement: (id, achievementId) => request('DELETE', `/api/persons/${id}/achievement/${achievementId}`),
  addLetter: (id, data) => request('POST', `/api/persons/${id}/letter`, data),
  deleteLetter: (id, letterId) => request('DELETE', `/api/persons/${id}/letter/${letterId}`),
  generateInvite: (id, email) => request('POST', `/api/persons/${id}/generate-invite`, { email }),
  transitionToMemorial: (id, data) => request('PUT', `/api/persons/${id}/memorial`, data),
  revertMemorial: (id) => request('PUT', `/api/persons/${id}/revert-memorial`, {}),
  submitTribute: (data) => request('POST', '/api/tributes', data, false),
  getTributes: (personId) => request('GET', `/api/tributes/person/${personId}`, null, false),
  addTribute: (id, data) => request('POST', `/api/persons/${id}/tribute`, data),
  approveTribute: (id, tributeId, approved) => request('PUT', `/api/persons/${id}/tribute/${tributeId}/approve`, { approved }),
  deleteTribute: (id, tributeId) => request('DELETE', `/api/persons/${id}/tribute/${tributeId}`),
  getFeed: () => request('GET', '/api/feed'),
  createFeedPost: (data) => request('POST', '/api/feed', data),
  editFeedPost: (id, data) => request('PUT', `/api/feed/${id}`, data),
  deleteFeedPost: (id) => request('DELETE', `/api/feed/${id}`),
  reactToPost: (id, emoji) => request('POST', `/api/feed/${id}/react`, { emoji }),
  commentOnPost: (id, text) => request('POST', `/api/feed/${id}/comment`, { text }),
  getUpcoming: () => request('GET', '/api/upcoming'),
  createRelationship: (data) => request('POST', '/api/relationships', data),
  createCheckoutSession: (data) => request('POST', '/api/create-checkout-session', data),
  submitSupport: (data) => request('POST', '/api/support', data),
  health: () => request('GET', '/api/health', null, false),
  // Cross-tree linking
  createCrossTreeLink: (data) => request('POST', '/api/cross-tree-link', data),
  getPendingCrossTreeLinks: () => request('GET', '/api/cross-tree-link/pending'),
  acceptCrossTreeLink: (id, data) => request('POST', `/api/cross-tree-link/${id}/accept`, data || {}),
  denyCrossTreeLink: (id) => request('POST', `/api/cross-tree-link/${id}/deny`),
  getLinkedFamilies: (personId) => request('GET', `/api/persons/${personId}/linked-families`, null, false),
  searchTrees: (query) => request('GET', `/api/trees/search/${encodeURIComponent(query)}`),
  // Sync linked families for existing multi-tree users
  syncLinkedFamilies: () => request('POST', '/api/sync-linked-families'),
  // Clean up stale cross-family references from previously deleted members
  cleanupStaleLinks: () => request('POST', '/api/cleanup-stale-links'),
  // Notifications
  getNotifications: () => request('GET', '/api/notifications'),
  markNotificationRead: (id) => request('PUT', `/api/notifications/${id}/read`),
  markAllNotificationsRead: () => request('PUT', '/api/notifications/read-all'),
  // AI Greeting Cards
  generateGreetingCard: (data) => request('POST', '/api/ai/generate-card', data),
  getCardUsage: () => request('GET', '/api/ai/card-usage'),
  // AI Legacy Writing Assistant
  legacyAssist: (data) => request('POST', '/api/ai/legacy-assist', data),
  // Tree member management
  getTreeMembers: (treeId) => request('GET', `/api/trees/${treeId}/members`),
  addTreeMember: (treeId, data) => request('POST', `/api/trees/${treeId}/members`, data),
  updateTreeMemberRole: (treeId, userId, role) => request('PUT', `/api/trees/${treeId}/members/${userId}/role`, { role }),
  removeTreeMember: (treeId, userId) => request('DELETE', `/api/trees/${treeId}/members/${userId}`),
  setTreeSuccessor: (treeId, userId) => request('PUT', `/api/trees/${treeId}/successor`, { userId }),
  // Tree merge
  mergeTreeInto: (sourceTreeId, targetTreeId) => request('POST', `/api/trees/${sourceTreeId}/merge-into/${targetTreeId}`),
  // Archive tree (soft delete)
  deleteTree: (treeId) => request('DELETE', `/api/trees/${treeId}`),
  restoreTree: (treeId) => request('POST', `/api/trees/${treeId}/restore`),
  permanentDeleteTree: (treeId) => request('DELETE', `/api/trees/${treeId}/permanent`),
  // Unlink person from a tree
  unlinkPerson: (personId, treeId) => request('DELETE', `/api/persons/${personId}/unlink/${treeId}`),
  // Tree preview (public)
  previewTree: (treeId) => request('GET', `/api/trees/${treeId}/preview`, null, false),
  // Join requests
  sendJoinRequest: (treeId, message) => request('POST', `/api/trees/${treeId}/join-request`, { message }),
  getJoinRequests: (treeId) => request('GET', `/api/trees/${treeId}/join-requests`),
  approveJoinRequest: (treeId, requestId, role) => request('POST', `/api/trees/${treeId}/join-requests/${requestId}/approve`, { role }),
  denyJoinRequest: (treeId, requestId) => request('POST', `/api/trees/${treeId}/join-requests/${requestId}/deny`),
  // Founding Family program
  checkFoundingCode: (code) => request('GET', `/api/founding/check/${encodeURIComponent(code)}`, null, false),
  redeemFoundingCode: (code) => request('POST', '/api/founding/redeem', { code }),
  generateFoundingCodes: (count, assignedTo) => request('POST', '/api/founding/generate', { count, assignedTo }),
  listFoundingCodes: () => request('GET', '/api/founding/list'),
  bulkSendFoundingInvites: (recipients) => request('POST', '/api/founding/bulk-send', { recipients }),
};

export default api;
